import React from 'react';
import withStyles from 'isomorphic-style-loader/withStyles';
import Hero from '../../components/marketing/Hero';
import StepList from '../../components/marketing/StepList';
import TrustBadges from '../../components/marketing/TrustBadges';
import CtaBanner from '../../components/marketing/CtaBanner';
import Link from '../../components/Link';
import s from './TrustSafety.css';

// LEGAL_REVIEW_REQUIRED: every trust/guarantee claim on this page is marketing copy and must be
// reviewed/approved by counsel before production. Claims are limited to what Paul confirmed (§10):
// ID verification + background checks + secure cashless Stripe payments. No platform-insurance claim.

// LEGAL_REVIEW_REQUIRED: describe the background check accurately; name provider/scope only if confirmed.
// COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
const VET_STEPS = [
  { title: 'A background check', text: 'Every pro confirms their identity before they can take any jobs.' },
  { title: 'Verification step', text: 'Pros complete a paid verification step as part of onboarding before they can take jobs.' },
  { title: 'Service skills & profile', text: 'Pros set the services they offer and build a reviewed profile.' },
  { title: 'Ongoing ratings review', text: 'We monitor ratings and reviews so quality stays high over time.' },
];

const PAYMENT = [
  { icon: 'M2 10h20M6 15h4m8-9H6a4 4 0 0 0-4 4v8a4 4 0 0 0 4 4h12a4 4 0 0 0 4-4V10a4 4 0 0 0-4-4Z', title: 'Cashless & in-app', text: 'No cash changes hands. Every payment runs securely through the app.' },
  { icon: 'M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6z', title: 'Pay when it’s done', text: 'Your card is on file, but you’re charged when the job is complete.' },
  { icon: 'M9 12l2 2 4-4M12 2 4 6v6c0 5 3.4 8 8 10 4.6-2 8-5 8-10V6z', title: 'Protected by design', text: 'Card details are handled by our payment processor, never stored in plain text.' },
  { icon: 'M21 11.5a8.4 8.4 0 0 1-.9 3.8A8.5 8.5 0 0 1 12.5 20a8.4 8.4 0 0 1-3.8-.9L3 21l1.9-5.7A8.4 8.4 0 0 1 4 11.5 8.5 8.5 0 0 1 11.5 3h.5A8.5 8.5 0 0 1 21 11z', title: 'Help if something’s off', text: 'Our team works with you to make it right if a job doesn’t go to plan.' },
];

const SAFETY = [
  { icon: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z', title: 'In-app chat', text: 'Message your pro in the app — no need to share your phone number.' },
  { icon: 'M12 8v4l3 2M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z', title: 'Live tracking', text: 'See your pro on the way and know when they’ll arrive.' },
  { icon: 'M3 5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2zM3 15l5-5 4 4 3-3 6 6', title: 'Photo documentation', text: 'Before-and-after photos give you a clear record of the work.' },
  { icon: 'M22 16.92v3a2 2 0 0 1-2.18 2 19.8 19.8 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.8 19.8 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72c.13.96.36 1.9.7 2.81a2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45c.9.34 1.85.57 2.81.7A2 2 0 0 1 22 16.92z', title: 'Support contact', text: 'Reach our team any time you need a hand during or after a job.' },
];

function TrustSafety() {
  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'BestHand',
    // COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod.
    description: 'On-demand home services marketplace. Pros are background-checked; payments are secure and cashless in-app.',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer support',
      email: 'support@besthandapp.com',
      telephone: '+1-267-215-1234',
    },
  };

  return (
    <main className={s.page}>
      {/* SECURITY: escape </script> breakouts in JSON-LD (defense-in-depth). */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd).replace(/</g, '\\u003c').replace(/>/g, '\\u003e').replace(/&/g, '\\u0026') }}
      />

      {/* COMPLIANCE: background-check claim is intentional (founder confirms BestHand performs background checks). LEGAL_REVIEW_REQUIRED before prod. */}
      <Hero
        eyebrow="Trust & Safety"
        title="Your safety is built in."
        subtitle="Every pro is background-checked. Every payment is secure and cashless. And if something isn’t right, our team helps make it right."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="How it works"
        secondaryTo="/how-it-works"
        badges={['Background-checked pros', 'Secure in-app payment', 'Satisfaction guarantee']}
      />

      <section className={s.sec}>
        <div className={s.wrap}>
          <h2 className={s.h2}>How we vet pros</h2>
          <p className={s.lead}>Before a pro can take a single job, they go through our vetting process.</p>
          <StepList steps={VET_STEPS} accent="green" columns={4} />
        </div>
      </section>

      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <TrustBadges heading="Payment protection" items={PAYMENT} />
          <p className={s.fine}>
            Payments are processed securely by{' '}
            <a href="https://stripe.com" target="_blank" rel="noopener noreferrer" className={s.inlineLink}>Stripe</a>.
            {' '}
            {/* LEGAL_REVIEW_REQUIRED: keep payment-protection wording aligned with Stripe’s actual role. */}
          </p>
        </div>
      </section>

      {/* Peace of Mind — REASSURING BUT DISCREET (Paul §10). Re-do guarantee is safe to state.
          Refunds phrased as case-by-case ("eligible issues are reviewed for a refund"), never
          "money-back guarantee" / "refund anytime". Do NOT claim platform insurance; pros carry
          their own. Do NOT publish any internal coverage figure. Direct per-job insurance is only
          softly teased as "expanding protections" — no terms, no dates. */}
      <section className={s.sec}>
        <div className={s.wrap}>
          <div className={s.pomCard}>
            <span className={s.pomTag}>Peace of Mind</span>
            <h2 className={s.pomTitle}>The BestHand guarantee</h2>
            {/* LEGAL_REVIEW_REQUIRED: exact guarantee wording & limits must be lawyer-approved before prod. */}
            <p className={s.pomLead}>
              Booked through BestHand and something’s off? We’ve got your back — our team helps make it
              right, a pro will redo the work, and eligible issues are reviewed for a refund. Pay safely
              in-app, every time.
            </p>
            <ul className={s.pomList}>
              <li>
                <strong>We help resolve issues.</strong> If a job doesn’t go to plan, our support team
                works with you to make it right.
              </li>
              <li>
                <strong>Re-do guarantee.</strong> If there’s a genuine problem with the work, a pro will
                come back and redo it.
              </li>
              <li>
                <strong>Eligible issues reviewed for a refund.</strong> Where appropriate, our team
                reviews your situation case by case.
              </li>
            </ul>
            <p className={s.pomNote}>
              We’re expanding protections over time to give you even more confidence on every booking.
            </p>
          </div>
        </div>
      </section>

      <section className={`${s.sec} ${s.alt}`}>
        <div className={s.wrap}>
          <TrustBadges heading="Safety features in every job" items={SAFETY} />
        </div>
      </section>

      <section className={s.sec}>
        <div className={s.wrap}>
          <div className={s.report}>
            <h2 className={s.h2}>Report a concern</h2>
            <p className={s.lead}>
              See something that doesn’t feel right? Tell us and we’ll look into it right away.
            </p>
            <div className={s.reportCtas}>
              <Link to="/contact-us" className={s.reportBtn}>Contact support</Link>
              <a href="mailto:support@besthandapp.com" className={s.reportLink}>support@besthandapp.com</a>
              <a href="tel:+12672151234" className={s.reportLink}>(267) 215-1234</a>
            </div>
          </div>
        </div>
      </section>

      <CtaBanner
        title="Book with confidence."
        subtitle="Verified pros, secure payments, and a team that has your back."
        primaryLabel="Book a pro"
        primaryTo="/category?type=all"
        secondaryLabel="See pricing"
        secondaryTo="/pricing"
      />
    </main>
  );
}

export default withStyles(s)(TrustSafety);
